import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, ArrowRight, ChevronRight, ChevronLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api`;

const EditCalendar = () => {
  const { token, isAdmin, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const { calendarId } = useParams();
  
  const [activeTab, setActiveTab] = useState('settings');
  const [calendar, setCalendar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Calendar Settings Form
  const [formData, setFormData] = useState({
    name: '',
    symbolic_name: '',
    type: '',
    location: '',
    active_status: true,
    multiple_servers: false,
    booking_type: 'fixed',
    office_hours: '',
    min_advance_booking_hours: 24,
    max_advance_booking_days: 14,
    cancellation_cutoff_hours: 48,
    cancellation_policy: ''
  });
  
  // Calendar Services (dual-list)
  const [availableServices, setAvailableServices] = useState([]);
  const [assignedServices, setAssignedServices] = useState([]);
  const [selectedAvailable, setSelectedAvailable] = useState([]);
  const [selectedAssigned, setSelectedAssigned] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);

  // Calendar Servers (dual-list)
  const [availableServers, setAvailableServers] = useState([]);
  const [assignedServers, setAssignedServers] = useState([]);
  const [selectedAvailableServers, setSelectedAvailableServers] = useState([]);
  const [selectedAssignedServers, setSelectedAssignedServers] = useState([]);
  const [serversLoading, setServersLoading] = useState(false);

  useEffect(() => {
    if (!isAdmin()) {
      navigate('/dashboard');
      return;
    }
    fetchCalendar();
  }, [calendarId]);

  const fetchCalendar = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/calendars/${calendarId}`, {
        headers: getAuthHeaders()
      });
      const cal = response.data.data;
      setCalendar(cal);
      
      setFormData({
        name: cal.name,
        symbolic_name: cal.symbolic_name || '',
        type: cal.type,
        location: cal.location,
        active_status: Boolean(cal.active_status),
        multiple_servers: Boolean(cal.multiple_servers),
        booking_type: cal.booking_type || 'fixed',
        office_hours: cal.office_hours,
        min_advance_booking_hours: cal.min_advance_booking_hours,
        max_advance_booking_days: cal.max_advance_booking_days,
        cancellation_cutoff_hours: cal.cancellation_cutoff_hours,
        cancellation_policy: cal.cancellation_policy || ''
      });
      
      // Fetch services if on services tab
      if (activeTab === 'services') {
        fetchCalendarServices();
      }
    } catch (err) {
      console.error('Error fetching calendar:', err);
      setError('Failed to load calendar');
    } finally {
      setLoading(false);
    }
  };

  const fetchCalendarServices = async () => {
    try {
      setServicesLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/calendar-services/${calendarId}/services`,
        { headers: getAuthHeaders() }
      );
      setAssignedServices(response.data.data.assigned || []);
      setAvailableServices(response.data.data.available || []);
    } catch (err) {
      console.error('Error fetching calendar services:', err);
      setError('Failed to load services');
    } finally {
      setServicesLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'services' && calendar) {
      fetchCalendarServices();
    }
    if (activeTab === 'servers' && calendar) {
      fetchCalendarServers();
    }
  }, [activeTab]);

  const fetchCalendarServers = async () => {
    try {
      setServersLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/calendar-servers/${calendarId}/servers`,
        { headers: getAuthHeaders() }
      );
      setAssignedServers(response.data.data.assigned || []);
      setAvailableServers(response.data.data.available || []);
    } catch (err) {
      console.error('Error fetching calendar servers:', err);
      setError('Failed to load servers');
    } finally {
      setServersLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmitSettings = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      const response = await axios.put(
        `${API_BASE_URL}/calendars/${calendarId}`,
        formData,
        { headers: getAuthHeaders() }
      );
      
      // Update the calendar state with the saved data
      setCalendar(response.data.data);
      
      setSuccess('Calendar settings updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating calendar:', err);
      const errorDetail = err.response?.data?.detail;
      if (Array.isArray(errorDetail)) {
        const errorMessages = errorDetail.map(err => err.msg || 'Validation error').join(', ');
        setError(errorMessages || 'Please check your input and try again.');
      } else {
        setError(errorDetail || 'Failed to update calendar');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitServices = async () => {
    setError('');
    setSaving(true);

    try {
      const serviceIds = assignedServices.map(s => s.id);
      await axios.put(
        `${API_BASE_URL}/calendar-services/${calendarId}/services`,
        { service_ids: serviceIds },
        { headers: getAuthHeaders() }
      );
      setSuccess('Calendar services updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating calendar services:', err);
      setError(err.response?.data?.detail || 'Failed to update services');
    } finally {
      setSaving(false);
    }
  };

  // Dual-list transfer functions
  const handleAddServices = () => {
    const toAdd = availableServices.filter(s => selectedAvailable.includes(s.id));
    setAssignedServices([...assignedServices, ...toAdd]);
    setAvailableServices(availableServices.filter(s => !selectedAvailable.includes(s.id)));
    setSelectedAvailable([]);
  };

  const handleRemoveServices = () => {
    const toRemove = assignedServices.filter(s => selectedAssigned.includes(s.id));
    setAvailableServices([...availableServices, ...toRemove].sort((a, b) => 
      a.service_name.localeCompare(b.service_name)
    ));
    setAssignedServices(assignedServices.filter(s => !selectedAssigned.includes(s.id)));
    setSelectedAssigned([]);
  };

  const toggleAvailableSelection = (serviceId) => {
    setSelectedAvailable(prev => 
      prev.includes(serviceId) 
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  const toggleAssignedSelection = (serviceId) => {
    setSelectedAssigned(prev => 
      prev.includes(serviceId) 
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  // Server handlers
  const handleSubmitServers = async () => {
    setError('');
    setSaving(true);

    try {
      const serverIds = assignedServers.map(s => s.id);
      await axios.post(
        `${API_BASE_URL}/calendar-servers/${calendarId}/servers`,
        { server_ids: serverIds },
        { headers: getAuthHeaders() }
      );
      setSuccess('Calendar servers updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating calendar servers:', err);
      setError(err.response?.data?.detail || 'Failed to update servers');
    } finally {
      setSaving(false);
    }
  };

  const handleAddServers = () => {
    const toAdd = availableServers.filter(s => selectedAvailableServers.includes(s.id));
    setAssignedServers([...assignedServers, ...toAdd]);
    setAvailableServers(availableServers.filter(s => !selectedAvailableServers.includes(s.id)));
    setSelectedAvailableServers([]);
  };

  const handleRemoveServers = () => {
    const toRemove = assignedServers.filter(s => selectedAssignedServers.includes(s.id));
    setAvailableServers([...availableServers, ...toRemove].sort((a, b) => 
      a.server_name.localeCompare(b.server_name)
    ));
    setAssignedServers(assignedServers.filter(s => !selectedAssignedServers.includes(s.id)));
    setSelectedAssignedServers([]);
  };

  const toggleAvailableServerSelection = (serverId) => {
    setSelectedAvailableServers(prev => 
      prev.includes(serverId) 
        ? prev.filter(id => id !== serverId)
        : [...prev, serverId]
    );
  };

  const toggleAssignedServerSelection = (serverId) => {
    setSelectedAssignedServers(prev => 
      prev.includes(serverId) 
        ? prev.filter(id => id !== serverId)
        : [...prev, serverId]
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6">
          <button
            onClick={() => navigate('/admin/calendars')}
            className="mb-6 flex items-center text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Manage Calendars
          </button>

          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-800">Edit Calendar</h1>
            <p className="text-gray-600 mt-2">{calendar?.name}</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
              {success}
            </div>
          )}

          {/* Tabs */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="border-b border-gray-200">
              <nav className="flex">
                <button
                  onClick={() => setActiveTab('settings')}
                  className={`px-6 py-4 text-sm font-medium transition-colors ${
                    activeTab === 'settings'
                      ? 'border-b-2 border-yellow-500 text-yellow-600'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  Calendar Settings
                </button>
                <button
                  onClick={() => setActiveTab('services')}
                  className={`px-6 py-4 text-sm font-medium transition-colors ${
                    activeTab === 'services'
                      ? 'border-b-2 border-yellow-500 text-yellow-600'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  Calendar Services
                </button>
                {formData.multiple_servers && (
                  <button
                    onClick={() => setActiveTab('servers')}
                    className={`px-6 py-4 text-sm font-medium transition-colors ${
                      activeTab === 'servers'
                        ? 'border-b-2 border-yellow-500 text-yellow-600'
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                  >
                    Calendar Servers
                  </button>
                )}
              </nav>
            </div>

            <div className="p-6">
              {/* Calendar Settings Tab */}
              {activeTab === 'settings' && (
                <form onSubmit={handleSubmitSettings} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Calendar Name
                    </label>
                    <div className="w-full px-4 py-2 border border-gray-200 rounded-md bg-gray-100 text-gray-600">
                      {formData.name}
                    </div>
                    <p className="mt-1 text-xs text-orange-600">
                      Calendar name cannot be changed after creation.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      URL (Symbolic Name)
                    </label>
                    <div className="w-full px-4 py-2 border border-gray-200 rounded-md bg-gray-100 text-gray-600">
                      {formData.symbolic_name}
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Auto-generated from calendar name. Cannot be changed.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Type *
                    </label>
                    <input
                      type="text"
                      name="type"
                      value={formData.type}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Location *
                    </label>
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Office Hours *
                    </label>
                    <input
                      type="text"
                      name="office_hours"
                      value={formData.office_hours}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Booking Type
                    </label>
                    <div className="w-full px-4 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-700 flex items-center justify-between">
                      <span>
                        {formData.booking_type === 'fixed' ? 'Fixed (Top-of-Hour)' : 'Floating (15-min Intervals)'}
                      </span>
                      <span className="text-xs text-gray-500 italic">
                        Cannot be changed after creation
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.booking_type === 'fixed' 
                        ? 'Appointments start at top of each hour (9:00, 10:00, 11:00...)'
                        : 'Appointments can start at any 15-minute interval (9:00, 9:15, 9:30...)'}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Min Advance Booking (hours) *
                      </label>
                      <input
                        type="number"
                        name="min_advance_booking_hours"
                        value={formData.min_advance_booking_hours}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                        min="0"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Max Advance Booking (days) *
                      </label>
                      <input
                        type="number"
                        name="max_advance_booking_days"
                        value={formData.max_advance_booking_days}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                        min="1"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Online Cancellation Cutoff (hours) *
                      </label>
                      <input
                        type="number"
                        name="cancellation_cutoff_hours"
                        value={formData.cancellation_cutoff_hours}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                        min="0"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cancellation Policy
                    </label>
                    <textarea
                      name="cancellation_policy"
                      value={formData.cancellation_policy}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                      rows="3"
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="active_status"
                      checked={formData.active_status}
                      onChange={handleChange}
                      className="w-4 h-4 text-yellow-500 border-gray-300 rounded focus:ring-yellow-500"
                    />
                    <label className="ml-2 text-sm font-medium text-gray-700">
                      Active (customers can browse and book)
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="multiple_servers"
                      checked={formData.multiple_servers}
                      onChange={handleChange}
                      className="w-4 h-4 text-yellow-500 border-gray-300 rounded focus:ring-yellow-500"
                    />
                    <label className="ml-2 text-sm font-medium text-gray-700">
                      Multiple Servers
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 ml-6 -mt-2">
                    Enable this if multiple servers can serve the same time slot. A slot remains available until all servers are booked.
                  </p>

                  <div className="flex justify-end pt-4">
                    <button
                      type="submit"
                      disabled={saving}
                      className="px-6 py-2 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {saving ? 'Saving...' : 'Save Settings'}
                    </button>
                  </div>
                </form>
              )}

              {/* Calendar Services Tab */}
              {activeTab === 'services' && (
                <div>
                  {servicesLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
                    </div>
                  ) : (
                    <>
                      <div className="mb-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-2">
                          Manage Services for this Calendar
                        </h3>
                        <p className="text-sm text-gray-600">
                          Select which of your services are available for booking on this calendar.
                        </p>
                      </div>

                      {/* Dual List - Horizontal Layout */}
                      <div className="flex flex-row gap-4 mb-6">
                        {/* Available Services List */}
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Available Services ({availableServices.length})
                          </label>
                          <div className="border border-gray-300 rounded-md h-96 overflow-y-auto bg-white">
                            {availableServices.length === 0 ? (
                              <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                                All services assigned
                              </div>
                            ) : (
                              <div className="divide-y divide-gray-200">
                                {availableServices.map(service => (
                                  <div
                                    key={service.id}
                                    onClick={() => toggleAvailableSelection(service.id)}
                                    className={`p-3 cursor-pointer transition-colors overflow-x-auto ${
                                      selectedAvailable.includes(service.id)
                                        ? 'bg-yellow-50 border-l-4 border-yellow-500'
                                        : 'hover:bg-gray-50'
                                    }`}
                                  >
                                    <div className="flex items-center min-w-0">
                                      <div
                                        className="w-3 h-3 rounded-full mr-3 flex-shrink-0"
                                        style={{ backgroundColor: service.color_code }}
                                      ></div>
                                      <div className="flex-1 min-w-0">
                                        <p className="font-medium text-gray-800 text-sm whitespace-nowrap overflow-x-auto">
                                          {service.service_name}
                                        </p>
                                        <p className="text-xs text-gray-500 whitespace-nowrap overflow-x-auto">
                                          ${service.service_price} • {service.service_duration} min
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Transfer Buttons */}
                        <div className="flex flex-col items-center justify-center gap-2 flex-shrink-0">
                          <button
                            type="button"
                            onClick={handleAddServices}
                            disabled={selectedAvailable.length === 0}
                            className="p-3 bg-yellow-500 hover:bg-yellow-600 text-white rounded-md disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            title="Add selected services"
                          >
                            <ChevronRight className="w-6 h-6" />
                          </button>
                          <button
                            type="button"
                            onClick={handleRemoveServices}
                            disabled={selectedAssigned.length === 0}
                            className="p-3 bg-gray-500 hover:bg-gray-600 text-white rounded-md disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            title="Remove selected services"
                          >
                            <ChevronLeft className="w-6 h-6" />
                          </button>
                        </div>

                        {/* Assigned Services List */}
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Assigned Services ({assignedServices.length})
                          </label>
                          <div className="border border-gray-300 rounded-md h-96 overflow-y-auto bg-white">
                            {assignedServices.length === 0 ? (
                              <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                                No services assigned
                              </div>
                            ) : (
                              <div className="divide-y divide-gray-200">
                                {assignedServices.map(service => (
                                  <div
                                    key={service.id}
                                    onClick={() => toggleAssignedSelection(service.id)}
                                    className={`p-3 cursor-pointer transition-colors overflow-x-auto ${
                                      selectedAssigned.includes(service.id)
                                        ? 'bg-yellow-50 border-l-4 border-yellow-500'
                                        : 'hover:bg-gray-50'
                                    }`}
                                  >
                                    <div className="flex items-center min-w-0">
                                      <div
                                        className="w-3 h-3 rounded-full mr-3 flex-shrink-0"
                                        style={{ backgroundColor: service.color_code }}
                                      ></div>
                                      <div className="flex-1 min-w-0">
                                        <p className="font-medium text-gray-800 text-sm whitespace-nowrap overflow-x-auto">
                                          {service.service_name}
                                        </p>
                                        <p className="text-xs text-gray-500 whitespace-nowrap overflow-x-auto">
                                          ${service.service_price} • {service.service_duration} min
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex justify-end pt-4">
                        <button
                          type="button"
                          onClick={handleSubmitServices}
                          disabled={saving}
                          className="px-6 py-2 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {saving ? 'Saving...' : 'Save Services'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Calendar Servers Tab */}
              {activeTab === 'servers' && (
                <div>
                  {serversLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
                    </div>
                  ) : (
                    <>
                      <div className="mb-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-2">
                          Manage Servers for this Calendar
                        </h3>
                        <p className="text-sm text-gray-600">
                          Select which servers can provide services on this calendar.
                        </p>
                      </div>

                      {/* Dual List - Horizontal Layout */}
                      <div className="flex flex-row gap-4 mb-6">
                        {/* Available Servers List */}
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Available Servers ({availableServers.length})
                          </label>
                          <div className="border border-gray-300 rounded-md h-96 overflow-y-auto bg-white">
                            {availableServers.length === 0 ? (
                              <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                                All servers assigned
                              </div>
                            ) : (
                              <div className="divide-y divide-gray-200">
                                {availableServers.map(server => (
                                  <div
                                    key={server.id}
                                    onClick={() => toggleAvailableServerSelection(server.id)}
                                    className={`p-3 cursor-pointer transition-colors overflow-x-auto ${
                                      selectedAvailableServers.includes(server.id)
                                        ? 'bg-yellow-50 border-l-4 border-yellow-500'
                                        : 'hover:bg-gray-50'
                                    }`}
                                  >
                                    <div className="flex items-center min-w-0">
                                      <div className="flex-1 min-w-0">
                                        <p className="font-medium text-gray-800 text-sm whitespace-nowrap overflow-x-auto">
                                          {server.server_name}
                                        </p>
                                        <p className="text-xs text-gray-500 whitespace-nowrap overflow-x-auto">
                                          {server.email}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Transfer Buttons */}
                        <div className="flex flex-col items-center justify-center gap-2 flex-shrink-0">
                          <button
                            type="button"
                            onClick={handleAddServers}
                            disabled={selectedAvailableServers.length === 0}
                            className="p-3 bg-yellow-500 hover:bg-yellow-600 text-white rounded-md disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            title="Add selected servers"
                          >
                            <ChevronRight className="w-6 h-6" />
                          </button>
                          <button
                            type="button"
                            onClick={handleRemoveServers}
                            disabled={selectedAssignedServers.length === 0}
                            className="p-3 bg-gray-500 hover:bg-gray-600 text-white rounded-md disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            title="Remove selected servers"
                          >
                            <ChevronLeft className="w-6 h-6" />
                          </button>
                        </div>

                        {/* Assigned Servers List */}
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Assigned Servers ({assignedServers.length})
                          </label>
                          <div className="border border-gray-300 rounded-md h-96 overflow-y-auto bg-white">
                            {assignedServers.length === 0 ? (
                              <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                                No servers assigned
                              </div>
                            ) : (
                              <div className="divide-y divide-gray-200">
                                {assignedServers.map(server => (
                                  <div
                                    key={server.id}
                                    onClick={() => toggleAssignedServerSelection(server.id)}
                                    className={`p-3 cursor-pointer transition-colors overflow-x-auto ${
                                      selectedAssignedServers.includes(server.id)
                                        ? 'bg-yellow-50 border-l-4 border-yellow-500'
                                        : 'hover:bg-gray-50'
                                    }`}
                                  >
                                    <div className="flex items-center min-w-0">
                                      <div className="flex-1 min-w-0">
                                        <p className="font-medium text-gray-800 text-sm whitespace-nowrap overflow-x-auto">
                                          {server.server_name}
                                        </p>
                                        <p className="text-xs text-gray-500 whitespace-nowrap overflow-x-auto">
                                          {server.email}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex justify-end pt-4">
                        <button
                          type="button"
                          onClick={handleSubmitServers}
                          disabled={saving}
                          className="px-6 py-2 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {saving ? 'Saving...' : 'Save Servers'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default EditCalendar;
